import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { MessagingConversationEntity } from './messaging-conversation.entity';
import { UserEntity } from './user.entity';

export enum MessagingParticipantType {
	USER = 'user',
	EXTERNAL = 'external',
	SYSTEM = 'system',
}

export enum MessagingParticipantRole {
	OWNER = 'owner',
	ADMIN = 'admin',
	MEMBER = 'member',
}

@Entity('_messaging_participant')
@Index(
	'idx_messaging_participant_conversation_user',
	['conversationId', 'userId'],
	{ unique: true, where: '"fk_user__id" IS NOT NULL' },
)
@Index(
	'idx_messaging_participant_conversation_external',
	['conversationId', 'externalIdentifier', 'externalIdentifierType'],
	{ unique: true, where: '"external_identifier" IS NOT NULL' },
)
export class MessagingParticipantEntity extends BaseEntity {
	@Column({
		name: 'fk_messaging_conversation__id',
		type: 'int',
		unsigned: true,
		nullable: false,
	})
	@Index()
	public conversationId!: number;

	@ManyToOne(() => MessagingConversationEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'CASCADE',
	})
	@JoinColumn({
		name: 'fk_messaging_conversation__id',
		referencedColumnName: 'id',
	})
	public conversation?: MessagingConversationEntity;

	@Column({
		name: 'participant_type',
		type: 'varchar',
		length: 50,
		nullable: false,
	})
	public participantType!: MessagingParticipantType;

	@Column({
		name: 'fk_user__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	@Index()
	public userId?: number;

	@ManyToOne(() => UserEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'CASCADE',
	})
	@JoinColumn({
		name: 'fk_user__id',
		referencedColumnName: 'id',
	})
	public user?: UserEntity;

	@Column({
		name: 'external_identifier',
		type: 'varchar',
		length: 255,
		nullable: true,
	})
	@Index()
	public externalIdentifier?: string;

	@Column({
		name: 'external_identifier_type',
		type: 'varchar',
		length: 50,
		nullable: true,
	})
	public externalIdentifierType?: string;

	@Column({
		name: 'display_name',
		type: 'varchar',
		length: 255,
		nullable: true,
	})
	public displayName?: string;

	@Column({
		name: 'role',
		type: 'varchar',
		length: 50,
		default: MessagingParticipantRole.MEMBER,
		nullable: false,
	})
	public role!: MessagingParticipantRole;

	@Column({
		name: 'is_active',
		type: 'boolean',
		default: true,
		nullable: false,
	})
	public isActive!: boolean;

	@Column({
		name: 'receive_notifications',
		type: 'boolean',
		default: true,
		nullable: false,
	})
	public receiveNotifications!: boolean;

	@Column({
		name: 'is_muted',
		type: 'boolean',
		default: false,
		nullable: false,
	})
	public isMuted!: boolean;

	@Column({
		name: 'fk_messaging_message__id_last_read',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public lastReadMessageId?: number;

	@Column({
		name: 'last_read_at',
		type: 'timestamp',
		nullable: true,
	})
	public lastReadAt?: Date;

	@Column({
		name: 'unread_count',
		type: 'int',
		unsigned: true,
		default: 0,
		nullable: false,
	})
	public unreadCount!: number;

	@Column({
		name: 'joined_at',
		type: 'timestamp',
		nullable: true,
	})
	public joinedAt?: Date;

	@Column({
		name: 'left_at',
		type: 'timestamp',
		nullable: true,
	})
	public leftAt?: Date;

	@Column({
		name: 'metadata',
		type: 'json',
		nullable: true,
	})
	public metadata?: Record<string, unknown>;
}
